/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-empty-interface */
import { types, SnapshotOut, Instance } from "mobx-state-tree";

import { PassingModel } from "./PassingModel";
import { Block, BlockModel } from "../Block/Block";
import { ContentImageModel } from "../ContentImage/ContentImage";
import { CourseModel } from "../Course/CourseModel";

export const UnitModel = types
  .model("UnitModel", {
    id: types.identifier,
    name: types.maybeNull(types.string),
    order: types.maybeNull(types.number),
    // Добавляется только в функциях checkExercise и getUnit_v3
    passing: types.maybeNull(PassingModel),

    course: types.maybeNull(types.late(() => types.reference(CourseModel))),
    blocks: types.optional(
      types.array(types.late(() => types.reference(BlockModel))),
      []
    ),

    // Используется только в упражнениях
    // TODO Удалить после перехода на новые контентные ручки
    // Отсюда берутся все картинки, нужно заменить на новые поля (pictureUrl и pictureUrlLongForExercise)
    contentImages: types.optional(types.array(ContentImageModel), []),
    // Нужно брать из пассингов
    // Total progress
    passingPercent: types.maybeNull(types.number),
    passingTestPercent: types.maybeNull(types.number),
  })
  .views((self) => ({
    get sortedBlocks(): Block[] {
      return self.blocks
        .slice()
        .sort((a, b) => Number(a.order) - Number(b.order));
    },
    get firstBlock(): Block | undefined {
      return self.blocks.length > 0 ? self.blocks[0] : undefined;
    },
  }))
  .views((self) => ({
    get firstBlock(): Block | undefined {
      return self.blocks.slice()[0];
    },
    get availableBlocksForTesting(): Block[] {
      return self.blocks.filter((block, i, arr) => {
        if (i === 0) {
          return true;
        }

        if (block.state !== "notStarted") {
          return true;
        }

        return arr[i - 1].state === "finished";
      });
    },
    getBlock(id: string | undefined): Block | undefined {
      return self.blocks.find((block) => block.id === id);
    },
  }))
  .views((self) => ({
    get nameWithoutUnit(): string {
      return self.name?.replace(/(Unit|Юнит)\s[0-9]+\./i, "") ?? "No name";
    },
    get exercisesCount(): number | null {
      const result = self.blocks.reduce(
        (acc, block) => acc + Number(block.exerciseCount),
        0
      );
      return result || null;
    },
    // Используется только в ModalTestUnitResult
    get totalProgress(): number {
      return (
        (self.course?.isForTesting
          ? self.passing?.passingTestPercent
          : self.passing?.passingPercent) ?? 0
      );
    },
    get testResults() {
      const testBlock = self.blocks.find(
        (block: Block) => block?.type?.code === "Test"
      );

      const completedExercises =
        testBlock?.exercises.filter((exercise) => exercise?.isPassed).length ||
        0;
      const exercisesCount = testBlock?.exercises.length || 0;
      if (!completedExercises || !exercisesCount) return 0;
      return Math.round((completedExercises / exercisesCount) * 100);
    },
    isBlockAvailableForTesting(blockId: string): boolean {
      return self.availableBlocksForTesting.some(({ id }) => id === blockId);
    },

    get totalBlocks(): number {
      return self.blocks.length;
    },
    get totalBlocksPassed(): number {
      return self.blocks.filter((blocks) => blocks.isPassed).length;
    },
    get totalBlocksWrong(): number {
      return self.blocks.filter((blocks) => blocks.isWrong).length;
    },
  }))
  .views((self) => ({
    get totalBlocksSkipped(): number {
      return (
        self.totalBlocks - (self.totalBlocksPassed + self.totalBlocksWrong)
      );
    },
    get isPassed(): boolean {
      if (self.totalBlocks === 0) return false;

      return (
        self.totalBlocksPassed === self.totalBlocks ||
        (self.totalBlocksPassed > 0 && self.totalBlocksWrong === 0)
      );
    },
    get firstNotPassedBlock(): Block | undefined {
      return self.sortedBlocks.find((block) => !block.isPassed);
    },
  }))
  .views((self) => ({
    get contentImageLongForExercise() {
      return self.contentImages.find((image) => image.isLongForExercise);
    },
    get contentImageForPreview() {
      return self.contentImages.find((image) => image.isForPreview);
    },
  }));

type UnitModelType = Instance<typeof UnitModel>;
export interface Unit extends UnitModelType {}
type UnitModelTypeSnapshotType = SnapshotOut<typeof UnitModel>;
export type UnitSnapshot = UnitModelTypeSnapshotType;
